import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Play, X } from 'lucide-react';

/**
 * The tour, stepped aside. Shown when it is paused or when the shop has wandered to a screen the
 * current step has nothing to say about — out of the way in a corner, above the phone's bottom
 * bar, one tap from picking up again and one from going for good.
 */
export function TourResumePill({
  onResume,
  onSkip,
}: {
  onResume: () => void;
  onSkip: () => void;
}): ReactElement {
  const { t } = useTranslation();
  return createPortal(
    <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-40 flex items-center rounded-full bg-primary text-primary-foreground shadow-lg animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none md:bottom-6 md:right-6">
      <button
        type="button"
        onClick={onResume}
        className="flex items-center gap-2 rounded-l-full py-2 pl-3 pr-2 text-sm font-semibold"
      >
        <span aria-hidden="true" className="relative flex size-2.5">
          <span className="absolute inline-flex size-full rounded-full bg-primary-foreground opacity-75 motion-safe:animate-ping" />
          <span className="relative inline-flex size-2.5 rounded-full bg-primary-foreground" />
        </span>
        <Play aria-hidden="true" className="size-3.5" />
        {t('onboarding.spotlight.resume')}
      </button>
      <button
        type="button"
        onClick={onSkip}
        aria-label={t('onboarding.spotlight.skip')}
        className="rounded-r-full border-l border-primary-foreground/30 py-2 pl-2 pr-3"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </div>,
    document.body,
  );
}
